import { Fragment, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  IconButton,
  Typography,
  Skeleton,
  Alert,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Tooltip,
} from '@mui/material';
import {
  ChevronRight as ChevronIcon,
  ContentCopy as CopyIcon,
  DataObject as RawIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RTooltip,
} from 'recharts';
import { useDeviceEvents, useDeviceHeartbeatCadence } from '@/hooks/use-device-analytics';
import { AccessibleChart, VisuallyHiddenTable } from '@/components/chart-a11y';
import type { AnalyticsWindow } from '@/lib/analytics-range';
import { displayDeviceSubType, displayDeviceType } from '@/lib/utils';
import {
  ACTIVITY_COLORS,
  ACTIVITY_LABELS,
  classifyEvent,
  formatEventTime,
  relativeTime,
  titleCase,
  type Severity,
} from '@/components/device-event-classify';

interface DeviceEventsProps {
  deviceId: string;
  /** devices.last_online (ISO) — drives the "Last online" summary. */
  lastOnline?: string;
  /** From heartbeat cadence — true means crash-loop/watchdog (a real fault). */
  degraded?: boolean;
  /**
   * Shared Analytics-tab window. When supplied the panel drops its own Card
   * chrome and its private 7-day window, and the health chip moves up to the
   * container's verdict strip. Omit it and the component behaves as before.
   */
  window?: AnalyticsWindow;
}

// Curated, human chips from event_data. The summary keys come FIRST — `reason`
// and `message` are what actually say why something happened, so a fault's error
// text is never crowded out by whatever key happens to be first in the object.
// Whatever doesn't fit is counted, not silently dropped: the row expands to the
// full payload, and `hidden` tells the operator there is more to see.
const SUMMARY_KEYS = ['message', 'reason', 'boot_reason', 'status'];
const MAX_CHIPS = 2;

function eventChips(
  eventType: string,
  data: Record<string, unknown> | null,
): { chips: string[]; hidden: number } {
  if (!data) return { chips: [], hidden: 0 };
  const fieldCount = Object.keys(data).length;

  // A boot event is the device announcing what it is, so the details read as
  // an identity: what kind of machine, which submodel, which model string,
  // which firmware. device_type and device_sub_type arrive as raw protocol
  // codes ("2", "3") and are mapped here — a bare number in this column tells
  // nobody anything. Sub-type and model are absent on older firmware and on
  // events written before the backend carried them, and simply don't render.
  if (eventType === 'boot') {
    const chips: string[] = [];
    if (typeof data.device_type === 'string' && data.device_type) {
      chips.push(displayDeviceType(data.device_type));
    }
    if (typeof data.device_sub_type === 'string') {
      const subType = displayDeviceSubType(data.device_sub_type);
      if (subType) chips.push(subType);
    }
    if (typeof data.device_model === 'string' && data.device_model) {
      chips.push(data.device_model);
    }
    if (typeof data.product_version === 'string') {
      chips.push(/^v/i.test(data.product_version) ? data.product_version : `v${data.product_version}`);
    }
    return { chips, hidden: Math.max(0, fieldCount - chips.length) };
  }

  const chips: string[] = [];
  for (const k of SUMMARY_KEYS) {
    if (chips.length >= MAX_CHIPS) break;
    const v = data[k];
    if (typeof v === 'string' && v) chips.push(`${titleCase(k)}: ${v}`);
  }
  for (const [k, v] of Object.entries(data)) {
    if (chips.length >= MAX_CHIPS) break;
    if (SUMMARY_KEYS.includes(k) || v == null || typeof v === 'object') continue;
    chips.push(`${titleCase(k)}: ${String(v)}`);
  }
  return { chips, hidden: Math.max(0, fieldCount - chips.length) };
}

/** Renders one event_data value; objects are pretty-printed rather than dropped. */
function formatValue(v: unknown): string {
  if (v === null) return 'null';
  if (typeof v === 'object') return JSON.stringify(v, null, 2);
  return String(v);
}

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
const DEFAULT_PAGE_SIZE = 5;

export function DeviceEvents({ deviceId, lastOnline, degraded, window: win }: DeviceEventsProps) {
  // 0-indexed to match TablePagination.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  // Which row is showing the unformatted payload. Keyed by row rather than a
  // boolean so opening a different event starts on the readable view again.
  const [rawRow, setRawRow] = useState<string | null>(null);
  const embedded = !!win;

  async function copyJson(rowKey: string, data: Record<string, unknown> | null) {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data ?? {}, null, 2));
      setCopied(rowKey);
      setTimeout(() => setCopied((c) => (c === rowKey ? null : c)), 1500);
    } catch {
      // Clipboard is unavailable over plain HTTP — the payload is still on screen.
    }
  }

  // Pin the window so the query key is stable: the shared one when embedded,
  // otherwise the standalone 7-day default pinned once per mount.
  const fallback = useMemo(() => {
    const now = new Date();
    return {
      start: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      end: now.toISOString(),
    };
  }, []);
  const start = win?.start ?? fallback.start;
  const end = win?.end ?? fallback.end;

  // Paging is per-window: a stale offset would land past the end of a shorter
  // one. Resetting during render (rather than in an effect) applies before the
  // children paint, so no request is ever made with the wrong offset.
  const [pagedWindow, setPagedWindow] = useState(`${start}|${end}`);
  if (pagedWindow !== `${start}|${end}`) {
    setPagedWindow(`${start}|${end}`);
    setPage(0);
    setExpanded(null);
  }

  const { data: eventsResponse, isLoading, error, refetch } = useDeviceEvents(deviceId, {
    start,
    end,
    limit: pageSize,
    offset: page * pageSize,
  });

  // The list query is paginated, so it only ever holds one page. The chart
  // needs the whole 7-day window — a separate, capped aggregate query. A single
  // device's presence events over a week stay well under this limit.
  const { data: allResponse } = useDeviceEvents(deviceId, {
    start,
    end,
    limit: 500,
    offset: 0,
  });
  const allEvents = useMemo(() => allResponse?.data ?? [], [allResponse]);

  // Heartbeat cadence carries the one fault signal that never lands in the
  // events table: a crash-loop (device beating >4× nominal). Fetched over the
  // same window so the health verdict can actually escalate rather than sitting
  // permanently green. The API caps windows at 14d, so skip longer ones instead
  // of firing a request that is guaranteed to fail.
  const { data: cadence } = useDeviceHeartbeatCadence(
    deviceId,
    { start, end, interval: win?.cadenceInterval ?? '1h' },
    { enabled: win ? win.cadenceEligible : true },
  );

  const events = eventsResponse?.data ?? [];
  const total = eventsResponse?.total ?? 0;

  // Bucket every event into a calendar day of the window, split by severity.
  // toDateString() collapses a timestamp to its local calendar day, matching the
  // bucket keys — no timezone math needed. The day count follows the window
  // rather than a fixed 7 so the chart never claims a period it didn't cover;
  // sub-day windows have no useful daily shape and skip the chart entirely
  // (the metric charts already mark each event on the shared timeline).
  const spanDays = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 86_400_000);
  const showDaily = spanDays >= 2;

  const dailyActivity = useMemo(() => {
    if (!showDaily) return [];
    const days: { key: string; label: string; routine: number; notable: number; fault: number }[] = [];
    const index = new Map<string, number>();
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    for (let i = spanDays - 1; i >= 0; i--) {
      const d = new Date(midnight);
      d.setDate(d.getDate() - i);
      index.set(d.toDateString(), days.length);
      days.push({
        key: d.toDateString(),
        // Weekday names repeat past a week — switch to a date once they would.
        label: spanDays > 7
          ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
          : d.toLocaleDateString(undefined, { weekday: 'short' }),
        routine: 0,
        notable: 0,
        fault: 0,
      });
    }
    for (const e of allEvents) {
      const idx = index.get(new Date(e.timestamp).toDateString());
      if (idx === undefined) continue;
      days[idx][classifyEvent(e.event_type, e.event_data).severity] += 1;
    }
    return days;
  }, [allEvents, showDaily, spanDays]);

  const activityTotals = useMemo(
    () =>
      dailyActivity.reduce(
        (a, d) => ({ routine: a.routine + d.routine, notable: a.notable + d.notable, fault: a.fault + d.fault }),
        { routine: 0, notable: 0, fault: 0 },
      ),
    [dailyActivity],
  );

  // Health verdict is computed over the WHOLE 7-day window (allEvents), not the
  // current page, so it stays stable as the operator pages through the list.
  // It escalates on a real crash-loop (cadence) or any non-routine event;
  // otherwise it reads Healthy.
  const isDegraded = !!degraded || !!cadence?.degraded;
  const lastUsed = allEvents.find((e) => e.event_type === 'boot' || e.event_type === 'presence_online');
  const hasIssue = allEvents.some((e) => classifyEvent(e.event_type, e.event_data).severity !== 'routine');
  const status = isDegraded
    ? { label: 'Crash-looping', color: 'error' as const }
    : hasIssue
      ? { label: 'Needs attention', color: 'warning' as const }
      : { label: 'Healthy', color: 'success' as const };

  // Embedded in the Analytics tab the container owns the card, the heading and
  // the health chip; standalone the component still draws all three itself.
  // Embedded there is no Card around this panel, so the table carries its own
  // frame — otherwise the rows float with nothing to read them against.
  const tableFrame = embedded
    ? { component: Paper, variant: 'outlined' as const, elevation: 0 }
    : {};

  const Shell = (embedded ? Box : Card) as React.ElementType;
  const shellProps = embedded ? {} : { variant: 'outlined' as const };
  const Body = (embedded ? Box : CardContent) as React.ElementType;

  return (
    <Shell {...shellProps}>
      {!embedded && (
        <CardHeader
          title="Recent Events"
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
          subheader="Device activity from the last 7 days"
          subheaderTypographyProps={{ variant: 'body2' }}
        />
      )}
      <Body>
        {/* Status summary — neutral while idle; only escalates on a real fault.
            Embedded, the verdict leads the whole tab instead, so only the
            "last used / last online" context stays here. */}
        {!isLoading && !error && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              {!embedded && (
                <Chip
                  size="small"
                  color={status.color}
                  variant={status.color === 'success' ? 'outlined' : 'filled'}
                  label={status.label}
                />
              )}
              {lastUsed && (
                <Tooltip title={formatEventTime(lastUsed.timestamp)}>
                  <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                    Last used {relativeTime(lastUsed.timestamp)}
                  </Typography>
                </Tooltip>
              )}
              {lastOnline && (
                <Typography variant="caption" color="text.secondary">
                  {lastUsed ? '· ' : ''}Last online {relativeTime(lastOnline)}
                </Typography>
              )}
            </Box>
            <Divider sx={{ mb: 1 }} />
          </>
        )}

        {/* Activity overview — events per day across the window, so the usage
            rhythm (and any problem days) is visible above the list. */}
        {!isLoading && !error && showDaily && allEvents.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <AccessibleChart
              label="Daily device activity over the last 7 days"
              description={`${activityTotals.routine + activityTotals.notable + activityTotals.fault} events, ${activityTotals.fault} problems`}
              sx={{ height: 140 }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyActivity} margin={{ top: 4, right: 8, left: -22, bottom: 0 }} barCategoryGap="22%">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEE" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={28} />
                  <RTooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Bar dataKey="routine" name="Activity" stackId="a" fill={ACTIVITY_COLORS.routine} />
                  <Bar dataKey="notable" name="Notable" stackId="a" fill={ACTIVITY_COLORS.notable} />
                  <Bar dataKey="fault" name="Problem" stackId="a" fill={ACTIVITY_COLORS.fault} />
                </BarChart>
              </ResponsiveContainer>
            </AccessibleChart>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center', mt: 0.5 }}>
              {(['routine', 'notable', 'fault'] as Severity[])
                .filter((s) => s === 'routine' || activityTotals[s] > 0)
                .map((s) => (
                  <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: ACTIVITY_COLORS[s] }} />
                    <Typography variant="caption" color="text.secondary">{ACTIVITY_LABELS[s]}</Typography>
                  </Box>
                ))}
            </Box>
            <VisuallyHiddenTable
              caption="Device events per day, last 7 days"
              columns={['Day', 'Activity', 'Notable', 'Problem']}
              rows={dailyActivity.map((d) => [d.label, d.routine, d.notable, d.fault])}
            />
            <Divider sx={{ mt: 1.5, mb: 1 }} />
          </Box>
        )}

        {isLoading && (
          <TableContainer {...tableFrame}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>Time</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton variant="circular" width={20} height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width={110} /></TableCell>
                    <TableCell><Skeleton variant="text" width="50%" /></TableCell>
                    <TableCell><Skeleton variant="text" width="40%" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Failed to load events. Please try again.
          </Alert>
        )}

        {!isLoading && !error && events.length === 0 && (
          <Alert severity="info">
            No activity in the last 7 days — this machine has simply been idle.
          </Alert>
        )}

        {events.length > 0 && (
          <>
            <TableContainer {...tableFrame}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Time</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map((event, idx) => {
                    const cls = classifyEvent(event.event_type, event.event_data);
                    const { chips, hidden } = eventChips(event.event_type, event.event_data);
                    const fault = cls.severity === 'fault';
                    const notable = cls.severity === 'notable';
                    const rowKey = `${event.timestamp}-${idx}`;
                    const open = expanded === rowKey;
                    const fields = Object.entries(event.event_data ?? {});
                    const tint = fault
                      ? 'rgba(230,10,50,0.06)'
                      : notable
                        ? 'rgba(243,167,46,0.08)'
                        : 'transparent';
                    return (
                      <Fragment key={rowKey}>
                        <TableRow
                          hover
                          onClick={() => setExpanded(open ? null : rowKey)}
                          aria-expanded={open}
                          sx={{ bgcolor: tint, cursor: 'pointer', '& > td': { borderBottom: open ? 0 : undefined } }}
                        >
                          <TableCell sx={{ verticalAlign: 'top', pr: 0 }}>
                            <IconButton
                              size="small"
                              aria-label={open ? 'Hide event details' : 'Show event details'}
                              onClick={(e) => { e.stopPropagation(); setExpanded(open ? null : rowKey); }}
                            >
                              <ChevronIcon
                                fontSize="small"
                                sx={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}
                              />
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', verticalAlign: 'top' }}>
                            <Tooltip title={relativeTime(event.timestamp)}>
                              <span style={{ cursor: 'help' }}>{formatEventTime(event.timestamp)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {cls.icon}
                              <Typography
                                variant="body2"
                                fontWeight={cls.severity === 'routine' ? 400 : 600}
                                color={fault ? 'error.main' : notable ? 'warning.main' : 'text.primary'}
                              >
                                {cls.label}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            {chips.length > 0 ? (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                {chips.map((c, i) => (
                                  <Chip key={i} size="small" variant="outlined" label={c} sx={{ height: 18, fontSize: 11 }} />
                                ))}
                                {/* Say how much is being withheld rather than
                                    truncating silently, and make the count the
                                    control that reveals it. The payload is
                                    already in hand — this expands, it does not
                                    fetch — but as plain grey text nobody read
                                    it as something they could click. */}
                                {hidden > 0 && (
                                  <Tooltip title={open ? 'Hide full payload' : 'Show full payload'}>
                                    <Button
                                      size="small"
                                      variant="text"
                                      disableRipple
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpanded(open ? null : rowKey);
                                      }}
                                      sx={{
                                        minWidth: 0,
                                        p: 0,
                                        fontSize: 11,
                                        lineHeight: 1.4,
                                        textTransform: 'none',
                                        color: 'text.secondary',
                                        '&:hover': { bgcolor: 'transparent', color: 'primary.main', textDecoration: 'underline' },
                                      }}
                                    >
                                      {open ? 'Hide payload' : `+${hidden} more`}
                                    </Button>
                                  </Tooltip>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                        </TableRow>

                        {/* Full event_data — including the object-valued fields and
                            the error text the chips can't carry. */}
                        <TableRow sx={{ bgcolor: tint }}>
                          <TableCell colSpan={4} sx={{ py: 0, borderBottom: open ? undefined : 0 }}>
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <Box
                                sx={{
                                  my: 1.5, p: 1.5,
                                  bgcolor: 'background.default',
                                  border: '1px solid', borderColor: 'divider', borderRadius: 1,
                                }}
                              >
                                {fields.length === 0 ? (
                                  <Typography variant="caption" color="text.disabled">
                                    This event carried no additional data.
                                  </Typography>
                                ) : rawRow === rowKey ? (
                                  // The exact object as stored, for pasting into
                                  // a ticket or diffing against another event —
                                  // the grid above reformats and reorders it.
                                  <Box
                                    component="pre"
                                    sx={{
                                      m: 0, fontFamily: 'monospace', fontSize: 12,
                                      lineHeight: 1.6, whiteSpace: 'pre', overflowX: 'auto',
                                    }}
                                  >
                                    {JSON.stringify(event.event_data ?? {}, null, 2)}
                                  </Box>
                                ) : (
                                  <Box
                                    component="dl"
                                    sx={{
                                      display: 'grid',
                                      gridTemplateColumns: 'minmax(120px, max-content) 1fr',
                                      gap: '4px 20px', m: 0,
                                    }}
                                  >
                                    {fields.map(([k, v]) => (
                                      <Fragment key={k}>
                                        <Box component="dt" sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>
                                          {k}
                                        </Box>
                                        <Box
                                          component="dd"
                                          sx={{
                                            m: 0, minWidth: 0,
                                            ...(v !== null && typeof v === 'object'
                                              ? { fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre', overflowX: 'auto' }
                                              : { fontSize: 13, wordBreak: 'break-word' }),
                                          }}
                                        >
                                          {formatValue(v)}
                                        </Box>
                                      </Fragment>
                                    ))}
                                  </Box>
                                )}
                                <Box
                                  sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    gap: 2, mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider',
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                    {event.event_type} · {new Date(event.timestamp).toISOString()}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {fields.length > 0 && (
                                      <Button
                                        size="small"
                                        startIcon={<RawIcon fontSize="small" />}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setRawRow((r) => (r === rowKey ? null : rowKey));
                                        }}
                                      >
                                        {rawRow === rowKey ? 'Formatted' : 'Full payload'}
                                      </Button>
                                    )}
                                    <Button
                                      size="small"
                                      startIcon={<CopyIcon fontSize="small" />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        void copyJson(rowKey, event.event_data);
                                      }}
                                    >
                                      {copied === rowKey ? 'Copied' : 'Copy JSON'}
                                    </Button>
                                  </Box>
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => { setPage(p); setExpanded(null); }}
              rowsPerPage={pageSize}
              rowsPerPageOptions={PAGE_SIZE_OPTIONS}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
                setExpanded(null);
              }}
              labelRowsPerPage="Rows per page"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count} events`}
              size="small"
            />
          </>
        )}
      </Body>
    </Shell>
  );
}
